import { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, AudioWaveform, ChevronRight, Upload, FileText, Check, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../components/Toaster/use-toast';
import './PracticeSetup.css';
import { getGroupMembers } from '../../api/groupApi';
import { createMeeting, assignMeeting } from '../../api/meetingApi';
import {
  meetingTypes,
  industries,
  getRolesForMeetingType,
  getSpeakingWithOptions,
  aiBehaviors,
  getGoalOptions,
  languageOptions,
} from '../../config/meetingOptions';
import AssigneeSelector from '../../components/AssigneeSelector/AssigneeSelector';

import { humanLabel, toISODate } from '../../utils/schedule';
import useScheduleMap from '../../hooks/useScheduleMap';
import SchedulePickerModal from '../../components/SchedulePickerModal/SchedulePickerModal';
import Dropdown from '../../components/Dropdown/Dropdown';
import { bytesToReadable, MAX_DOCS, WEB_ACCEPT_ATTR } from '../../utils/uploadLimits';
import AdditionalInfoField from '../../components/AdditionalInfoField/AdditionalInfoField';
import GoalsField from './components/GoalsField';
import { useDispatch, useSelector } from 'react-redux';
import useDocumentUploader from '../../hooks/useDocumentUploader';
import { getInitials } from '../../utils/getInitials';
import { startLoading, stopLoading, selectIsLoading, selectLoadingMessage } from '../../store/uiSlice';
import { getRealtimeEntitlements } from '../../api/realtimeApi';
import { isRealtimeUpgradeRequired } from '../../utils/realtimeUpgradeState';

import LoadingOverlay from '../../components/Loading/LoadingOverlay';
import VoicePickerDialog from '../../components/VoicePicker/VoicePickerDialog';
import { getVoiceProfile } from '../../config/voiceProfiles';

export default function PracticeSetup({ meeting , currentAssignments = [],meetingType, onBack, onAssigned, organizationId, groupId }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state } = useLocation();
  const [members, setMembers] = useState([]);
  const currentUser = useSelector((s) => s.user?.data);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const isGroupMode = Boolean(organizationId && groupId)
  const [assignToEveryone, setAssignToEveryone] = useState(false);
  const dispatch = useDispatch();

const CREATE_KEY = "practiceSetup:createMeeting";
const ASSIGN_KEY = "practiceSetup:assignMeeting";

const isCreating = useSelector(selectIsLoading(CREATE_KEY));
const isAssigning = useSelector(selectIsLoading(ASSIGN_KEY));
const loadingMsg = useSelector(selectLoadingMessage(isCreating ? CREATE_KEY : ASSIGN_KEY));

const isBusy = isCreating || isAssigning;

const {
  documents,
  docFiles,
  uploadErrors,
  handleFiles,
  removeDocument,
  buildFormData,
} = useDocumentUploader({ toast });
  // const isEditMode = Boolean(meeting);
  const didHydrateRef = useRef(false);
  const primaryCtaLabel = organizationId ? 'Assign Meeting' : 'Start Practice';
  const normalizeMeetingType = (mt) => {
    if (!mt) return null;
    return typeof mt === 'string'
      ? meetingTypes.find(t => t.value === mt) ?? null
      : mt; // already an object
  };
  const meetingTypeData =
    normalizeMeetingType(meetingType) ||
    normalizeMeetingType(state?.meetingType) ||
    null
  const [schedulingFor, setSchedulingFor] = useState(null); // userId|null
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [dateISO, setDateISO] = useState('');
  const { scheduledMap, setFor, clearFor } = useScheduleMap();
  const [timeHHmm, setTimeHHmm] = useState('');
  const todayISO = toISODate(new Date());

  const [formData, setFormData] = useState({
    meetingType: meetingTypeData?.value ?? '',
    industry: '',
    userRole: '',
    speakingWith: '',
    customMeeting: '',
    customIndustry: '',
    customRole: '',
    customSpeakingWith: '',
    goals: [],
    customGoals: [],
    languageLabel: "English",
    aiBehavior: 'professional',
    duration: "",
    documents: [],
    aiVoice: 'cedar', // default male
    additionalInfo: '',
    privacyNotification: false,
  });
  const [invalid, setInvalid] = useState({});
  const durations = [
    { value: '5', label: '5 min' },
    { value: '10', label: '10 min' },
    { value: '15', label: '15 min' },
    { value: '30', label: '30 min' },

  ];
  // Memoize options for better perf
  const roles = useMemo(() => getRolesForMeetingType(formData.meetingType), [formData.meetingType]);
  const speakingWithOptions = useMemo(() => getSpeakingWithOptions(formData.meetingType, formData.userRole), [formData.meetingType, formData.userRole]);
  const goals = useMemo(() => getGoalOptions(formData.meetingType, formData.userRole, formData.industry), [formData.meetingType, formData.userRole, formData.industry]);
  const goalResetKey = `${formData.meetingType}|${formData.industry}|${formData.userRole}|${formData.speakingWith}`;
console.log("group id", groupId)
  const handleGoalsChange = (nextGoals) => {
    setFormData((prev) => ({ ...prev, goals: nextGoals }));
  };
  const handleCustomGoalsChange = (nextCustomGoals) => {
    setFormData((prev) => ({ ...prev, customGoals: nextCustomGoals }));
  };
  const patchGoalInvalid = (patch) => {
    setInvalid((prev) => ({ ...prev, ...patch }));
  };
  const handleDuplicateGoal = () => {
    toast({
      title: 'Duplicate goal',
      description: 'That goal is already selected.',
      variant: 'warning'
    });
  };
  const getApiErrorDescription = (error, fallback = 'Could not submit. Please try again.') => {
    const details = Array.isArray(error?.response?.data?.details) ? error.response.data.details : [];
    if (details[0]?.message) return details[0].message;
    return error?.response?.data?.error || error?.message || fallback;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInvalid(prev => ({ ...prev, [name]: false }));

    if (name === "meetingType") {
      setFormData(prev => ({
        ...prev,
        meetingType: value,
        industry: "",
        userRole: "",
        speakingWith: "",
        goals: [],
        customMeeting: "",
        customIndustry: "",
        customRole: "",
        customSpeakingWith: "",
        customGoals: [],
      }));
      
      setInvalid(prev => ({
        ...prev,
        industry: false, userRole: false, speakingWith: false,
        customMeeting: false, customIndustry: false, customRole: false,
        customSpeakingWith: false, customGoals: false
      }));
    } else if (name === "industry") {
      setFormData(prev => ({
        ...prev,
        industry: value,
        userRole: "",
        speakingWith: "",
        goals: [],
        customIndustry: "",
        customRole: "",
        customSpeakingWith: "",
        customGoals: [],
      }));
      setInvalid(prev => ({
        ...prev,
        userRole: false, speakingWith: false,
        customIndustry: false, customRole: false, customSpeakingWith: false, customGoals: false
      }));
    } else if (name === "userRole") {
      setFormData(prev => ({
        ...prev,
        userRole: value,
        speakingWith: "",
        goals: [],
        customRole: "",
        customSpeakingWith: "",
        customGoals: [],
      }));
      setInvalid(prev => ({
        ...prev,
        speakingWith: false, customRole: false, customSpeakingWith: false, customGoals: false
      }));
    } else if (name === "speakingWith") {
      setFormData(prev => ({
        ...prev,
        speakingWith: value,
        goals: [],
        customSpeakingWith: "",
        customGoals: [],
      }));
      setInvalid(prev => ({ ...prev, customSpeakingWith: false, customGoals: false }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };


  useEffect(() => {
    didHydrateRef.current = false;
  }, [meeting?.id]);
  
  useEffect(() => {
    if (!groupId) {
      console.warn("groupId is undefined or null");
      return;
    }
  
    const fetchGroupMembers = async () => {
      try {
        const data = await getGroupMembers(organizationId, groupId);
        console.log("group members", data)
        const filteredMembers = data.items.filter(member => member.id !== currentUser.id);
        setMembers(filteredMembers);
      } catch (error) {
        console.error("Failed to fetch group members:", error);
        setMembers([]);
      }
    };
  
    fetchGroupMembers();


  }, [groupId, organizationId]);
  // useEffect(() => {
  //   if (!meeting) return;
  //   if (meeting) {
  //     // Pre-fill form with existing meeting data
  //     setFormData({
  //       meetingType: meeting.meetingType || '',
  //       industry: meeting.industry || '',
  //       userRole: meeting.userRole || '',
  //       speakingWith: meeting.speakingWith || '',
  //       customMeeting: meeting.customMeeting || '',
  //       customIndustry: meeting.customIndustry || '',
  //       customRole: meeting.customRole || '',
  //       customSpeakingWith: meeting.customSpeakingWith || '',
  //       goals: meeting.goals || [],
  //       customGoals: meeting.customGoals || [],
  //       aiBehavior: meeting.aiBehavior || 'professional',
  //       duration: String(meeting.duration || ''),
  //       documents: [], // not editing docs yet
  //       aiVoice: meeting.aiVoice || 'cedar',
  //       additionalInfo: meeting.additionalInfo || '',
  //     });
  //   }

  // }, [meeting]);

  // useEffect(() => {
  //   if (!isEditMode) return;
  //   if (!meeting?.id) return;
  //   if (!currentAssignments?.length) return;
  
  //   // ✅ only hydrate once per "edit session"
  //   if (didHydrateRef.current) return;
  //   didHydrateRef.current = true;
  
  //   // 1) set selected assignees
  //   setSelectedAssignees(currentAssignments.map(a => a.userId));
  
  //   // 2) seed schedule map from DB values
  //   currentAssignments.forEach((a) => {
  //     if (!a?.scheduledFor) return;
  //     const { dateISO, timeHHmm } = isoToLocalDateTime(a.scheduledFor);
  //     setFor(a.userId, dateISO, timeHHmm);
  //   });
  // }, [isEditMode, meeting?.id]); 
  const computeInvalid = () => {
    const v = {};

    const effectiveMeetingType = formData.meetingType === 'custom' ? (formData.customMeeting || '').trim() : (formData.meetingType || '');
    const effectiveIndustry = formData.industry === 'custom' ? (formData.customIndustry || '').trim() : (formData.industry || '');
    const effectiveUserRole = formData.userRole === 'custom' ? (formData.customRole || '').trim() : (formData.userRole || '');
    const effectiveSpeakingWith = formData.speakingWith === 'custom' ? (formData.customSpeakingWith || '').trim() : (formData.speakingWith || '');

    const anyPresetGoals = (formData.goals || []).length > 0;
    const cleanedCustom = (formData.customGoals || [])
      .map(g => (g || '').trim())
      .filter(Boolean);

    // backend requires 2–200 chars per custom goal
    const invalidCustomItem = cleanedCustom.some(g => g.length < 2 || g.length > 200);
    const anyCustomGoals = cleanedCustom.length > 0;

    v.meetingType = !effectiveMeetingType;
    v.industry = !effectiveIndustry;
    v.userRole = !effectiveUserRole;
    v.speakingWith = !effectiveSpeakingWith;
    v.languageLabel = !formData.languageLabel;

    // 🔴 key part: require at least one preset OR one valid custom
    v.goals = !(anyPresetGoals || anyCustomGoals);
    // mark customGoals invalid only when present but out of length bounds
    v.customGoals = invalidCustomItem;

    v.aiBehavior = !formData.aiBehavior;
    v.duration = !formData.duration;
    v.additionalInfo = (formData.additionalInfo?.length || 0) > 4000;

    return v;
  };

  const hasInvalid = (v) => Object.values(v).some(Boolean);
  const handleStartPractice = async () => {
    const v = computeInvalid();
    setInvalid(v);
    console.log('Invalid flags:', v);
    if (hasInvalid(v)) {
      toast({
        title: 'Validation Error',
        description: 'Please fill the required fields.',
        variant: 'destructive'
      });
      // optional: scroll to first invalid
      const firstKey = Object.keys(v).find(k => v[k]);
      if (firstKey) {
        document.querySelector(`[data-field="${firstKey}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      return;
    }
    const presetGoals = (formData.goals || []).filter(
      g => g && g.trim().toLowerCase() !== 'custom goal'
    );

    const customGoalsClean = (formData.customGoals || [])
      .map(g => (g || '').trim())
      .filter(g => g.length >= 2 && g.length <= 200);
    // build shared payload once
    const effectiveMeetingType = formData.meetingType === 'custom' ? (formData.customMeeting || '').trim() : (formData.meetingType || '');
    const effectiveIndustry = formData.industry === 'custom' ? (formData.customIndustry || '').trim() : (formData.industry || '');
    const effectiveUserRole = formData.userRole === 'custom' ? (formData.customRole || '').trim() : (formData.userRole || '');
    const effectiveSpeakingWith = formData.speakingWith === 'custom' ? (formData.customSpeakingWith || '').trim() : (formData.speakingWith || '');

    const base = {
      meetingType: effectiveMeetingType,
      industry: effectiveIndustry,
      userRole: effectiveUserRole,
      speakingWith: effectiveSpeakingWith,
      goals: presetGoals,
      customGoals: customGoalsClean,
      languageLabel: formData.languageLabel || "English",
      aiBehavior: formData.aiBehavior,
      duration: parseInt(formData.duration, 10), // must be number
      aiVoice: formData.aiVoice || 'cedar',
      additionalInfo: formData.additionalInfo || "",
 
    };

    const payload = buildFormData({ ...base, createdWithAi: false });

    
    const effectiveAssigneeIds = assignToEveryone
  ? members.map(m => Number(m.id))          // members already excludes self in your fetch
  : selectedAssignees.map(id => Number(id));
  
  const assignments = effectiveAssigneeIds.map(uid => ({
    forUserId: uid,
    scheduledFor: scheduledMap[uid]?.iso || null,
  }));
  
        
    try {
      // if (isEditMode) {
      //   await updateAssignedPracticeMeeting(organizationId, groupId, meeting.id, {
      //     ...base,
      //     assignments,
      //   });
      //   toast({ title: "Meeting updated" });
      //   onAssigned?.();
      // } else 
      if (isGroupMode) {
        // Group mode
        if (!assignToEveryone && selectedAssignees.length === 0) {
          toast({
            title: 'Select assignees',
            description: 'Pick at least one team member (or enable "Assign to everyone").',
            variant: 'destructive'
          });
          return;
        }
        


        const fd = buildFormData({
          ...base,
          organizationId,
          groupId,
          assignToEveryone,
          assignments,          // array
          createdWithAi: false, // optional
        });
        dispatch(startLoading({ key: ASSIGN_KEY, message: "Assigning meeting…" }));
        try {
          const res = await assignMeeting(fd);
        
          toast({
            title: 'Assigned',
            description: assignToEveryone
              ? `Assigned to ${members.length} members.`
              : `Assigned to ${res?.count ?? selectedAssignees.length} ${((res?.count ?? selectedAssignees.length) === 1) ? 'person' : 'people'}.`,
            duration: 4000,
          });
        
          onAssigned?.(res);
        } finally {
          dispatch(stopLoading(ASSIGN_KEY));
        }
      }else{ 
        dispatch(startLoading({ key: CREATE_KEY, message: "Creating meeting…" }));
        try {
          const entitlements = await getRealtimeEntitlements();
          if (entitlements?.canUseRealtime === false) {
            navigate('/pricing');
            return;
          }

          const meeting = await createMeeting(payload);
          navigate(`/meeting/${meeting.id}`);
        } finally {
          dispatch(stopLoading(CREATE_KEY));
        }
        
      }

   
    } catch (error) {
      console.error(error);
      if (isRealtimeUpgradeRequired(error)) {
        navigate('/pricing');
        return;
      }

      toast({
        title: 'Error',
        description: getApiErrorDescription(error),
        variant: 'destructive',
      });
    }
  };
  


  return (
    
    <div className="pm-setup-container">
          {isBusy && <LoadingOverlay text={loadingMsg || "Working…"} />}
      <div className="pm-header">
        <button className="pm-back-btn" onClick={onBack}>
          <ArrowLeft className="pm-btn-icon" />
          Back
        </button>
        <div>
          <h1 className="pm-header-title">
            Setup {formData.customMeeting || meetingTypes.find(type => type.value === formData.meetingType)?.label || 'Practice'} Conversation
          </h1>
          <p className="pm-header-desc">Configure your AI-powered practice simulation</p>
        </div>
      </div>

      <div className="content-grid">
        <div className="pm-main">
          {/* Context */}
          <div className="pm-context-card">
            <div className="pm-card-content">
              {/* Meeting Type */}
              <div className="pm-field">
                <label htmlFor="meetingType">Conversation Type</label>
                <Dropdown
                  options={meetingTypes.map(t => ({ value: t.value, label: t.label }))}
                  value={formData.meetingType || null}
                  onChange={(v) => handleChange({ target: { name: "meetingType", value: v } })}
                  placeholder="Select Conversation type…"
                  invalid={!!invalid.meetingType}
                />
              </div>

              {formData.meetingType === 'custom' && (
                <div className="pm-field">
                  <label htmlFor="customMeeting">Describe what type of meeting this is</label>
                  <input
                    id="customMeeting"
                    name="customMeeting"
                    placeholder="e.g., Project Kickoff"
                    value={formData.customMeeting}
                    onChange={handleChange}
                    className={`pm-input ${invalid.meetingType ? 'is-invalid' : ''}`}
                  />
                </div>
              )}

              {/* Industry */}
              <div className="pm-field">
                <label htmlFor="industry">Industry</label>


                <Dropdown
                  options={industries.map(opt => ({ value: opt.value, label: opt.label }))}
                  value={formData.industry || null}
                  onChange={(v) => handleChange({ target: { name: 'industry', value: v } })}
                  placeholder="Select industry…"
                  disabled={!formData.meetingType}
                  ariaLabel="Industry"
                  invalid={!!invalid.industry}
                />
              </div>

              {formData.industry === 'custom' && (
                <div className="pm-field">
                  <label htmlFor="customIndustry">Custom Industry</label>
                  <input
                    id="customIndustry"
                    name="customIndustry"
                    placeholder="e.g., Aerospace"
                    value={formData.customIndustry}
                    onChange={handleChange}

                    className={`pm-input ${invalid.industry ? 'is-invalid' : ''}`}
                    aria-invalid={invalid.industry ? 'true' : 'false'}
                  />
                </div>
              )}

              {/* Role */}
              <div className="pm-field">
                <label htmlFor="userRole">Your Role</label>
                <Dropdown
                  options={roles.map(opt => ({ value: opt.value, label: opt.label }))}
                  value={formData.userRole || null}
                  onChange={(v) => handleChange({ target: { name: 'userRole', value: v } })}
                  placeholder="Select role…"
                  disabled={!formData.industry}
                  ariaLabel="Your Role"
                  invalid={!!invalid.userRole}
                />
              </div>

              {formData.userRole === 'custom' && (
                <div className="pm-field">
                  <label htmlFor="customRole">What is Your Role in this Meeting</label>
                  <input
                    id="customRole"
                    name="customRole"
                    placeholder="e.g., Data Engineer"
                    value={formData.customRole}
                    onChange={handleChange}
                    className={`pm-input ${invalid.userRole ? 'is-invalid' : ''}`}
                    aria-invalid={invalid.userRole ? 'true' : 'false'}
                  />
                </div>
              )}

              {/* Speaking With */}
              <div className="pm-field">
                <label htmlFor="speakingWith">Who are you speaking with?</label>
                <Dropdown
                  options={speakingWithOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                  value={formData.speakingWith || null}
                  onChange={(v) => handleChange({ target: { name: 'speakingWith', value: v } })}
                  placeholder="Select contact…"
                  disabled={!formData.userRole}
                  ariaLabel="Speaking With"
                  invalid={!!invalid.speakingWith}
                />
              </div>

              {formData.speakingWith === 'custom' && (
                <div className="pm-field">
                  <label htmlFor="customSpeakingWith">Describe Who You're Speaking With</label>
                  <input
                    id="customSpeakingWith"
                    name="customSpeakingWith"
                    placeholder="e.g., Project Sponsor"
                    value={formData.customSpeakingWith}
                    onChange={handleChange}
                    className={`pm-input ${invalid.speakingWith ? 'is-invalid' : ''}`}
                    aria-invalid={invalid.speakingWith ? 'true' : 'false'}
                  />
                </div>
              )}

              {/* Goals */}
              <GoalsField
                options={goals}
                goals={formData.goals}
                customGoals={formData.customGoals}
                invalidGoals={!!invalid.goals}
                invalidCustomGoals={!!invalid.customGoals}
                onGoalsChange={handleGoalsChange}
                onCustomGoalsChange={handleCustomGoalsChange}
                onInvalidPatch={patchGoalInvalid}
                onDuplicateGoal={handleDuplicateGoal}
                resetKey={goalResetKey}
              />


                <AdditionalInfoField
                  value={formData.additionalInfo || ''}
                  invalid={!!invalid.additionalInfo}
                  onChange={(val) => {
                    setFormData((p) => ({ ...p, additionalInfo: val }));
                    setInvalid((prev) => ({
                      ...prev,
                      additionalInfo: val.length > 4000,
                    }));
                  }}
                  wrapperClassName="pm-field"
                  counterClassName="pm-counter"
                  textareaClassName="pm-textarea"
                />
            </div>
          </div>

          {/* Session Settings */}
          <div className="pm-settings-card">
            <div className="pm-card-header">
              <h3 className="pm-card-title">Session Settings</h3>
              <p className="card-description">Configure AI behavior and session details</p>
            </div>
            <div className="pm-card-content">
              {/* AI Behavior */}
              <div className="pm-field">
                <label htmlFor="aiBehavior">How should the AI Behave?</label>
                <Dropdown
                  options={aiBehaviors}  // ← keep full objects (value, label, help)
                  value={formData.aiBehavior || null}
                  onChange={(v) => handleChange({ target: { name: 'aiBehavior', value: v } })}
                  placeholder="Choose behavior…"
                  ariaLabel="AI Behavior"
                  invalid={!!invalid.aiBehavior}
                />
              </div>
              <div className="pm-field">
                <label>Voice</label>
                <button
                  aria-haspopup="dialog"
                  className="pm-voice-selector"
                  onClick={() => setVoicePickerOpen(true)}
                  type="button"
                >
                  <AudioWaveform aria-hidden="true" size={22} />
                  <span className="pm-voice-selector-copy">
                    <strong>Choose a voice</strong>
                    <span>{getVoiceProfile(formData.aiVoice).label} selected</span>
                  </span>
                  <ChevronRight aria-hidden="true" size={19} />
                </button>
              </div>
              <div className="pm-field">
                <label htmlFor="languageLabel">Language</label>
                <Dropdown
                  options={languageOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
                  value={formData.languageLabel || "English"}
                  onChange={(v) => handleChange({ target: { name: 'languageLabel', value: v } })}
                  placeholder="Select language…"
                  ariaLabel="Language"
                  invalid={!!invalid.languageLabel}
                />
              </div>
              {/* Duration */}
              <div
                className={`pm-field`}
                data-field="duration"
              >
                <label>How long is this Practice Meeting?</label>
                <div className="pm-duration-buttons">
                  {durations.map(duration => {
                    const selected = formData.duration === duration.value;
                    return (
                      <button
                        key={duration.value}
                        type="button"
                        className={`pm-duration-badge ${selected ? 'selected' : ''} ${invalid.duration ? 'is-invalid' : ''
                          }`}
                        aria-pressed={selected}
                        onClick={() => {
                          setFormData(prev => ({ ...prev, duration: duration.value }));
                          setInvalid(prev => ({ ...prev, duration: false }));
                        }}
                      >
                        {selected && <Check className="pm-check-icon" aria-hidden="true" />}
                        {duration.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Document Upload */}
              <div className="pm-field">
                <label>Documents you want to upload (Optional)</label>
                <div className="pm-upload">
                  <Upload className="pm-upload-icon" />
                  <p className="pm-upload-text">Drag and drop files here or click to upload</p>
                  <p className="pm-upload-text">
                    Supported: <strong>PDF, DOCX, PPTX, text files</strong><br />
                    Up to <strong>2 files</strong> total, <strong>10&nbsp;MB total</strong>
                  </p>
                  {documents.length > 0 && (
  <p className="pm-upload-text" style={{ marginTop: 6 }}>
    Files: <strong>{documents.length}</strong> / {MAX_DOCS} &nbsp;•&nbsp;
  </p>
)}

                  <input
                    id="documents"
                    type="file"
                    multiple
                    accept={WEB_ACCEPT_ATTR}
                    onChange={async (e) => {
                      if (e.target.files?.length) await handleFiles(e.target.files);
                      e.target.value = ""; // allow re-upload same file
                    }}
                    className="pm-upload-input"
                  />
                  <button
                    type="button"
                    className="pm-upload-btn"
                    onClick={() => document.getElementById('documents').click()}
                  >
                    Select Files
                  </button>
                  {/* Inline error list */}
                  {uploadErrors.length > 0 && (
                    <div className=" pm-upload-text pm-upload-errors">
                      <div>
                        {uploadErrors.map((err, i) => (
                          <p className='pm-upload-error-message' key={i}>{err}</p>
                        ))}
                      </div>
                    </div>
                  )}

{documents.map((doc) => (
  <div key={doc.id} className="pm-doc">
    <div className="pm-doc-details">
      <FileText className="pm-doc-icon" />
      <span className="pm-doc-name">{doc.name}</span>
    </div>

    <span className="pm-doc-size">{bytesToReadable(doc.size)}</span>

    <button
      type="button"
      className="pm-remove-doc"
      onClick={() => removeDocument(doc.id)}
      aria-label={`Remove ${doc.name}`}
      title="Remove"
    >
      ×
    </button>
  </div>
))}

                </div>
              </div>
            </div>
          </div>

          {/* Org-only */}
      {/* Org-only */}
{organizationId && (
  <div className="pm-field">
    {/* Switch FIRST */}
    <div className="pm-assign-all-row">
      <div>
        <div className="pm-assign-all-title">Assign to everyone in group</div>
        <div className="pm-muted">Automatically assign this meeting to all group members.</div>
      </div>

      <label className="pm-switch" aria-label="Assign to everyone in group">
        <input
          type="checkbox"
          checked={assignToEveryone}
          onChange={(e) => {
            const next = e.target.checked;
            setAssignToEveryone(next);

            if (next) {
              // hide selector → clear any individual picks
              setSelectedAssignees([]);
              // optional: also clear any per-user schedules you may have set
              // (if you want, we can add a "clearAll" helper to useScheduleMap)
            }
          }}
        />
        <span className="pm-switch-track" />
      </label>
    </div>

    {/* Assignee selector only when NOT assigning to everyone */}
    {!assignToEveryone && (
      <>
        <label style={{ marginTop: 12 }}>Assignees </label>
        <AssigneeSelector
          members={members}
          value={selectedAssignees}
          onChange={setSelectedAssignees}
          placeholder="Type a name or email…"
        />
        {selectedAssignees.length === 0 && (
          <div className="pm-muted" style={{ marginTop: 6 }}>Pick at least one member</div>
        )}
      </>
    )}

    {/* When assign-to-all is ON, show the “affected users” summary */}
    {assignToEveryone && (
      <div className="pm-assign-all-summary">
        <strong>{members.length}</strong> member{members.length === 1 ? "" : "s"} will be assigned this meeting.
      </div>
    )}
  </div>
)}

{organizationId && !assignToEveryone && selectedAssignees.length > 0 && (
            <div className="pm-assignee-schedule-list">
              {selectedAssignees.map(uid => {
                const m = members.find(m => m.id === uid);
                const sched = scheduledMap[uid];
                return (
                  <div key={uid} className="pm-assignee-row">
                    <div className="pm-assignee-ident">
                      <div className="pm-assignee-avatar">
                      {getInitials(m)}
                      </div>
                      <div>
                        <div className="pm-assignee-name">{m?.firstName} {m?.lastName}</div>
                        <div className="pm-assignee-sub">{m?.email}</div>
                      </div>
                    </div>
                    <div className="pm-assignee-actions">
                      {sched?.iso && <span className="pm-assignee-when">{humanLabel(sched.dateISO, sched.time)}</span>}

                      <div className="pm-when-container">
                        <button
                          type="button"
                          className="pm-when-chip"
                          onClick={() => {
                            setSchedulingFor(uid);
                            setDateISO(sched?.dateISO || '');
                            setTimeHHmm(sched?.time || '');
                          }}
                        >
                          {sched?.iso ? 'Reschedule' : 'Schedule for'}
                        </button>
                        {sched?.iso && (
                          <button className="pm-clear-when" onClick={() => clearFor(uid)} aria-label="Clear schedule"><X /></button>
                        )}
                      </div>


                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Sticky CTA */}
        <div className="pm-button-content">
          <div className="pm-actions">
            <button
              className="action-button"
              onClick={handleStartPractice}
              disabled={isBusy}
            >
  {isBusy ? "Please wait…" : primaryCtaLabel}
            </button>
          </div>
        </div>
      </div>

      <SchedulePickerModal
        open={Boolean(schedulingFor)}
        title="Schedule for member"
        dateISO={dateISO}
        timeHHmm={timeHHmm}
        onChangeDate={setDateISO}
        onChangeTime={setTimeHHmm}
        minDateISO={todayISO}
        onClose={() => setSchedulingFor(null)}
        onSave={() => {
          const iso = new Date(`${dateISO}T${timeHHmm}:00`).toISOString();
          if (new Date(iso) <= new Date()) {
            toast({ title: 'Invalid time', description: 'Pick a future time.', variant: 'destructive' });
            return;
          }
          setFor(schedulingFor, dateISO, timeHHmm);
          setSchedulingFor(null);
        }}
      />
      <VoicePickerDialog
        onClose={() => setVoicePickerOpen(false)}
        onSelect={(aiVoice) => setFormData((prev) => ({ ...prev, aiVoice }))}
        open={voicePickerOpen}
        selectedVoice={formData.aiVoice}
      />
    </div>
  );
}
